import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Divider,
} from '@mui/material';
import { ArrowBack, CheckCircle, AddPhotoAlternate, Close } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubmitReport } from '../../hooks/useReport';
import type { ReportCategory } from '../../types/report';

const CATEGORIES: Array<{ value: ReportCategory; label: string; urgent: boolean }> = [
  { value: 'FAKE_PROFILE', label: '허위 프로필 / 사진 도용', urgent: false },
  { value: 'STALKING', label: '스토킹 / 반복 연락', urgent: true },
  { value: 'SEXUAL_CONTENT', label: '성적 메시지 / 음란 콘텐츠', urgent: true },
  { value: 'ROMANCE_SCAM', label: '금전 요구 / 로맨스 스캠', urgent: true },
  { value: 'ILLEGAL_SOLICITATION', label: '다단계 / 종교 / 불법 권유', urgent: false },
  { value: 'EXTERNAL_MESSENGER', label: '외부 메신저 전환 강요', urgent: false },
  { value: 'VERBAL_ABUSE', label: '욕설 / 위협 / 협박', urgent: true },
  { value: 'OTHER', label: '기타', urgent: false },
];

const ReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportedId = searchParams.get('targetId') ?? '';
  const cardId = searchParams.get('cardId') ?? undefined;

  const [category, setCategory] = useState<ReportCategory | ''>('');
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const { submit, loading, error } = useSubmitReport();

  const handleImageAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = 3 - images.length;
    const toAdd = files.slice(0, remaining);
    setImages((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => setPreviews((prev) => [...prev, ev.target?.result as string]);
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleImageRemove = (idx: number) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!category || !reportedId) return;
    const result = await submit({
      reportedId: Number(reportedId),
      category,
      description: description.trim() || undefined,
      cardId: cardId ? Number(cardId) : undefined,
      attachments: images.length > 0 ? images : undefined,
    });
    if (result) setSubmitted(true);
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" elevation={0}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
              <ArrowBack />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>신고하기</Typography>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>신고가 접수되었습니다</Typography>
          <Typography sx={{ color: 'text.secondary', mb: 4, fontSize: '1rem', lineHeight: 1.8 }}>
            처리 결과를 서비스 내 알림으로 안내드리겠습니다.
          </Typography>
          {/* TODO: 유저 간 차단 기능 구현 후 "상대방 차단하기" 버튼 추가 */}
          <Button
            variant="outlined"
            fullWidth
            size="large"
            sx={{ py: 1.5, fontSize: '1rem' }}
            onClick={() => navigate(-1)}
          >
            확인
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>신고하기</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!reportedId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            채팅방 또는 프로필에서 신고해 주세요.
          </Alert>
        )}

        {/* 신고 사유 */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>
          신고 사유를 선택해주세요 <span style={{ color: 'red' }}>*</span>
        </Typography>
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
          <RadioGroup value={category} onChange={(e) => setCategory(e.target.value as ReportCategory)}>
            {CATEGORIES.map((cat, idx) => (
              <React.Fragment key={cat.value}>
                {idx > 0 && <Divider />}
                <FormControlLabel
                  value={cat.value}
                  control={<Radio sx={{ color: 'primary.main' }} />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                      <Typography sx={{ fontSize: '1rem' }}>{cat.label}</Typography>
                      {cat.urgent && (
                        <Chip label="긴급" size="small" color="error" sx={{ height: 20, fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  }
                  sx={{ mx: 0, px: 2, py: 0.5, minHeight: 52 }}
                />
              </React.Fragment>
            ))}
          </RadioGroup>
        </Paper>

        {/* 상세 설명 */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
          상세 설명 <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.9rem' }}>(선택)</Typography>
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="상세한 내용을 입력해 주세요 (최대 500자)"
          value={description}
          onChange={(e) => {
            if (e.target.value.length <= 500) setDescription(e.target.value);
          }}
          inputProps={{ style: { fontSize: '1rem' } }}
          helperText={`${description.length}/500`}
          FormHelperTextProps={{ sx: { textAlign: 'right' } }}
          sx={{ mb: 3 }}
        />

        {/* 증거 첨부 */}
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
          증거 첨부 <Typography component="span" sx={{ fontWeight: 400, color: 'text.secondary', fontSize: '0.9rem' }}>(선택, 최대 3장)</Typography>
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          {previews.map((src, idx) => (
            <Box key={idx} sx={{ position: 'relative', width: 80, height: 80 }}>
              <img
                src={src}
                alt={`첨부 ${idx + 1}`}
                style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
              />
              <IconButton
                size="small"
                onClick={() => handleImageRemove(idx)}
                sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: 0.25, border: '1px solid', borderColor: 'divider' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {images.length < 3 && (
            <label>
              <input type="file" accept="image/*" multiple hidden onChange={handleImageAdd} />
              <Box
                sx={{
                  width: 80, height: 80, border: '1px dashed', borderColor: 'divider',
                  borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', '&:hover': { borderColor: 'primary.main' },
                }}
              >
                <AddPhotoAlternate sx={{ color: 'text.secondary' }} />
              </Box>
            </label>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={!category || !reportedId || loading}
          onClick={handleSubmit}
          sx={{ py: 1.75, fontSize: '1.05rem', fontWeight: 700 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : '신고 접수하기'}
        </Button>
      </Container>
    </Box>
  );
};

export default ReportPage;
